import { TokenInfo } from "../types"
import { WhitelistStatus, WhitelistStatuses } from "@/contracts/hooks/useWhitelist"
import { TransactionResult } from "@/contracts/types"
import { INotifyTxCallbacks, useWalletContext } from "@firestarter-private/firestarter-library/lib/hooks"
import BigNumber from "bignumber.js"
import { useCallback, useEffect, useMemo, useState } from "react"
import { balanceToNumber, balanceToNumeric, numericToUint256 } from "@/utils/balanceFormatter"
import { Divider } from "@/components/layout/Divider"
import { balanceToCurrency, numberToBalance, numericToBalance } from "@firestarter-private/firestarter-library/lib/utils/bigNumbers"
import { IdentitiesInput } from "./IdentitiesInput"
import { IdentitiesPresaleApi } from "@/api/identities-presale/identities-presale-api"
import { IdentityPriceRange } from "@/api/identities-presale/types"
import { IdentitiesList } from "./IdentitiesList"
import './SwapForm.scss'
import { ApprovalSteps, RoundButton } from "@/components/common"
import classNames from "classnames"
import { getStringTxError } from "@/contracts/hooks/utils"

interface Props {
  totalSwapAmount: BigNumber
  fundsSwapped: BigNumber
  publicMaxAllocation: BigNumber
  privateMaxAllocation: BigNumber
  exchangeRate: BigNumber
  privatePresaleAllowed: boolean
  swappedByUser: BigNumber
  swappedPrivateByUser: BigNumber
  swappedPublicByUser: BigNumber
  balance: BigNumber
  fundsDecimals: number
  allowance: BigNumber
  whiteListStatus: WhitelistStatus | null
  projectId: string
  fundToken: TokenInfo
  isPrivatePhaseInProgress: boolean
  isPublicPhaseInProgress: boolean
  isClosePeriod?: boolean
  isOpenAccess?: boolean
  isSingleApproval?: boolean
  isEthProject?: boolean
  identitiesApiUrl?: string
  onApprove: (amount?: string, callbacks?: INotifyTxCallbacks) => Promise<void>
  onDeposit: (amount: string, callbacks?: INotifyTxCallbacks) => Promise<TransactionResult | undefined>
  onDepositPrivate: (amount: string, callbacks?: INotifyTxCallbacks) => Promise<TransactionResult | undefined>
}

export const SwapIdentitiesForm: React.FC<Props> = ({
  publicMaxAllocation,
  privateMaxAllocation,
  privatePresaleAllowed,
  swappedByUser,
  swappedPrivateByUser,
  swappedPublicByUser,
  balance,
  fundsDecimals,
  allowance,
  whiteListStatus,
  fundToken,
  isPrivatePhaseInProgress,
  isPublicPhaseInProgress,
  isClosePeriod,
  isEthProject,
  isOpenAccess,
  identitiesApiUrl,
  onApprove,
  onDeposit,
  onDepositPrivate,
}) => {
  const { account } = useWalletContext()

  const maxAllocationForOpenAccess = useMemo(() => {
    return numberToBalance(2500, fundsDecimals)
  }, [fundsDecimals])

  const allowedInClosePeriod = useMemo(() => {
    return (privatePresaleAllowed || !privateMaxAllocation.isZero()) || (!privateMaxAllocation.isZero() && !publicMaxAllocation.isZero())
  }, [
    privatePresaleAllowed,
    privateMaxAllocation,
    publicMaxAllocation,
  ])

  const availableAllocation = useMemo(() => {
    if (isPrivatePhaseInProgress) {
      return privateMaxAllocation.minus(swappedPrivateByUser)
    }
    if (isOpenAccess) {
      return maxAllocationForOpenAccess.minus(swappedByUser)
    }
    if (isClosePeriod && allowedInClosePeriod) {
      return publicMaxAllocation.plus(privateMaxAllocation).minus(swappedByUser)
    }
    if (isPublicPhaseInProgress && allowedInClosePeriod) {
      return new BigNumber(0)
    }
    return publicMaxAllocation.minus(swappedPublicByUser)
  }, [
    publicMaxAllocation,
    privateMaxAllocation,
    swappedPublicByUser,
    swappedPrivateByUser,
    swappedByUser,
    isPrivatePhaseInProgress,
    isOpenAccess,
    isPublicPhaseInProgress,
    isClosePeriod,
    allowedInClosePeriod,
    maxAllocationForOpenAccess,
  ])

  const maxSwapAmount = useMemo(() => {
    return balanceToNumeric(
      BigNumber.min(
        availableAllocation,
        balance,
      ),
      fundsDecimals
    );
  }, [
    availableAllocation,
    balance,
    fundsDecimals,
  ])

  const [isApproving, setIsApproving] = useState(false)
  const [isSwapping, setIsSwapping] = useState(false)
  const [swappingError, setSwappingError] = useState<string | undefined>()
  const [identities, setIdentities] = useState<{ name: string, price: number, discountedPrice: number }[]>([])
  const [priceRanges, setPriceRanges] = useState<IdentityPriceRange[]>([])
  const [presaleDiscount, setPresaleDiscount] = useState<number>(0)
  
  const identitiesPresaleApi = useMemo(() => {
    return identitiesApiUrl ? new IdentitiesPresaleApi(identitiesApiUrl) : undefined
  }, [identitiesApiUrl])

  useEffect(() => {
    if (identitiesPresaleApi) {
      identitiesPresaleApi.getIdentityPrices().then((res) => {
        if (res) {
          setPriceRanges(res.data.priceRanges)
          setPresaleDiscount(res.data.presaleDiscount)
        }
      })
    }
  }, [identitiesPresaleApi])

  const handleAddIdentity = (identity: { name: string, price: number, discountedPrice: number }) => {
    setIdentities(prev => [...prev, identity])
  }

  const handleRemoveIdentity = (name: string) => {
    setIdentities(prev => prev.filter(identity => identity.name !== name))
  }

  const totalPrice = useMemo(() => {
    return identities.reduce((acc, identity) => {
      return acc + identity.price
    }, 0)
  }, [identities])

  const amountToSwap = useMemo(() => {
    return identities.reduce((acc, identity) => {
      return acc + identity.discountedPrice
    }, 0).toString()
  }, [identities])
  
  const disableApproval = useMemo(() => {
    if (isOpenAccess) {
      return allowance.isGreaterThanOrEqualTo(numericToBalance(amountToSwap, fundsDecimals))
        || +amountToSwap <= 0
        || isApproving
    }
    return whiteListStatus !== WhitelistStatuses.passed
      || allowance.isGreaterThanOrEqualTo(numericToBalance(amountToSwap, fundsDecimals))
      || +amountToSwap <= 0
      || isApproving
  }, [  
    whiteListStatus,
    allowance,
    amountToSwap,
    fundsDecimals,
    isApproving,
    isOpenAccess,
  ])

  const disableSwapping = useMemo(() => {
    const disableForAllPhases = () => {
      return (
        +amountToSwap <= 0 ||
        (isEthProject && !balanceToNumber(allowance, fundsDecimals)) ||
        numericToBalance(maxSwapAmount, fundsDecimals).isLessThan(numericToBalance(amountToSwap, fundsDecimals)) ||
        (isEthProject && allowance.isLessThan(numericToBalance(amountToSwap, fundsDecimals))) ||
        isSwapping
      )
    }

    const disableForPrivatePhase = () => {
      return disableForAllPhases() || whiteListStatus !== WhitelistStatuses.passed || !privatePresaleAllowed
    }

    const disableForPublicPhase = () => {
      return (
        disableForAllPhases() ||
        whiteListStatus !== WhitelistStatuses.passed ||
        (!isClosePeriod && allowedInClosePeriod)
      )
    }

    const disableForClosePeriod = () => {
      return disableForAllPhases() || whiteListStatus !== WhitelistStatuses.passed || availableAllocation.isZero()
    }

    if (isPrivatePhaseInProgress) return disableForPrivatePhase()
    if (isOpenAccess) return disableForAllPhases()
    if (isClosePeriod) return disableForClosePeriod()
    if (isPublicPhaseInProgress) return disableForPublicPhase()
  }, [
    isPrivatePhaseInProgress,
    isOpenAccess,
    isClosePeriod,
    isPublicPhaseInProgress,
    amountToSwap,
    isEthProject,
    allowance,
    fundsDecimals,
    maxSwapAmount,
    isSwapping,
    whiteListStatus,
    privatePresaleAllowed,
    allowedInClosePeriod,
    availableAllocation,
  ])

  const warningMessage = useMemo(() => {
    if (isOpenAccess) {
      if (numericToBalance(amountToSwap, fundsDecimals).isGreaterThan(availableAllocation)) {
        return 'The amount exceeds your allocation.'
      }
      return
    }
    if (publicMaxAllocation.isZero() && privateMaxAllocation.isZero()) {
      return `Your wallet didn't win to have access for this sale`
    }
    if (numericToBalance(amountToSwap, fundsDecimals).isGreaterThan(availableAllocation)) {
      return 'The amount exceeds your allocation.'
    }
    if (numericToBalance(amountToSwap, fundsDecimals).isGreaterThan(balance)) {
      return 'The amount exceeds your balance of the tokens'
    }
    if (isPrivatePhaseInProgress && !privatePresaleAllowed) {
      return 'The sale for FCFS is not open yet'
    }
    if (isPublicPhaseInProgress && !isClosePeriod && publicMaxAllocation.isZero()) {
      return 'The sale by Guaranteed access is already over'
    }
  }, [
    amountToSwap,
    availableAllocation,
    balance,
    fundsDecimals,
    isClosePeriod,
    isPrivatePhaseInProgress,
    isPublicPhaseInProgress,
    privateMaxAllocation,
    privatePresaleAllowed,
    publicMaxAllocation,
    isOpenAccess,
  ])

  const handleApprove = useCallback(async () => {
    if (disableApproval) return
    setIsApproving(true)
    await onApprove(numericToUint256(amountToSwap, fundsDecimals))
    setIsApproving(false)
  }, [disableApproval, onApprove, amountToSwap, fundsDecimals])

  const handleSwap = useCallback(async () => {
    if (disableSwapping || !identitiesPresaleApi) return
    setIsSwapping(true)
    setSwappingError(undefined)

    const createdPurchase = await identitiesPresaleApi.createIdentityPurchase({
      identityNames: identities.map((identity) => identity.name),
      walletAddress: account!,
      investedAmount: numericToUint256(amountToSwap, fundsDecimals),
      decimals: Number(fundsDecimals),
      allocation: availableAllocation.toFixed(0, 1),
    })
    if (createdPurchase && 'error' in createdPurchase) {
      setSwappingError(createdPurchase.error)
      setIsSwapping(false)
      return
    }

    const depositMethod = isPrivatePhaseInProgress ? onDepositPrivate : onDeposit
    const result = await depositMethod(amountToSwap, {
      onHash: () => setIdentities([]),
    })
    setIsSwapping(false)

    if (result && 'error' in result) {
      setSwappingError(getStringTxError(result.error))
    }
    
    if (result && 'txHash' in result && result.txHash) {
      await identitiesPresaleApi.updateIdentityPurchase({
        purchaseId: createdPurchase.data.purchaseId,
        txHash: result.txHash,
        status: 'completed',
      })
    }
  }, [
    disableSwapping,
    identitiesPresaleApi,
    identities,
    account,
    amountToSwap,
    fundsDecimals,
    availableAllocation,
    isPrivatePhaseInProgress,
    onDepositPrivate,
    onDeposit,
  ])

  return (
    <form className="swap-form tile">
      <div className="info-list">
        <div>
          <dt className="name">Available swap amount</dt>
          <dd className="value">
            <big>{balanceToCurrency(availableAllocation, fundsDecimals)}</big> {fundToken.name}
          </dd>
        </div>
      </div>
      <Divider />
      <div className="swap-form__controls">
        {identitiesPresaleApi && (
          <IdentitiesInput
            priceRanges={priceRanges}
            presaleDiscount={presaleDiscount}
            identitiesList={identities}
            identitiesPresaleApi={identitiesPresaleApi}
            onAddIdentity={handleAddIdentity}
          />
        )}
        <IdentitiesList 
          identities={identities}
          totalPrice={totalPrice}
          totalDiscountedPrice={Number(amountToSwap)}
          onRemoveIdentity={handleRemoveIdentity}
        />
        {!!warningMessage && (
          <div className="form-message form-message--warning text-center">
            <span>{warningMessage}</span>
          </div>
        )}
        <div className="swap-form__buttons">
          {isEthProject && (
            <RoundButton size="large" disabled={disableApproval} onClick={handleApprove} loading={isApproving}>
              Approve
            </RoundButton>
          )}
          <RoundButton size="large" disabled={disableSwapping} onClick={handleSwap} loading={isSwapping}>
            SWAP
          </RoundButton>
        </div>
        {isEthProject && <ApprovalSteps fillingCondition={disableApproval} />}
      </div>
      <div
        className={classNames('form-message form-message--error form-message--no-padding text-center mt-3', {
          hidden: !swappingError,
        })}
      >
        <span>{swappingError}</span>
      </div>
    </form>
  )
}
